<script lang="ts">
  /* The acknowledgement, and the reason the whole app is allowed to exist.
   *
   * ═══ ONE COPY, BECAUSE TWO WOULD DRIFT ═══
   *
   * On a desktop this floats at the bottom-right of the chart. On a phone it is
   * the dock's last line. Those are two positions, not two notices — a licence
   * condition that is worded differently depending on which screen you are
   * holding is a licence condition somebody has stopped reading.
   *
   * The comment below travels with the markup for the same reason the styles
   * do. It is the only record of why this line may not be shortened.
   */
  import type { Snippet } from 'svelte';

  let {
    inDock = false,
    lead
  }: {
    inDock?: boolean;
    /** Rendered at the start of the desktop credit block. */
    lead?: Snippet;
  } = $props();
</script>

<!-- Attribution is not decoration here. The shipped datasets are CHS, DFO and
     Environment Canada under the Open Government Licence — Canada, which
     permits redistribution with acknowledgement, and the chart itself is the
     DFO/CHS ENC service. NOAA's charts and tide predictions are US Government
     work and carry no restriction, but they are named for the same reason:
     somebody reading a height off this chart is entitled to know whose it is.

     A STATIC LINE, not one assembled from the visible layers. Attribution that
     comes and goes as rows are switched is attribution somebody can miss.

     Open-Meteo supplies the wind forecast and CAPE field under CC BY 4.0.
     "Adapted" records the CAPE-to-colour transformation and may not be
     shortened out of the static credit below.

     RainViewer requires its linked "Weather data by RainViewer" credit to
     remain visible whenever radar is available. It may not be shortened out
     of the static credit below.

     NOAA GOES-19 GLM supplies the observed lightning points. "Adapted"
     records the filtering, rolling window and chart rendering.

     ═══ THE ENC PROXY IS LICENSED, AND THIS IS WHAT MAKES IT SO ═══

     PLAN.md left "may a PUBLIC app proxy the CHS ENC WMS" open and said to
     settle it before going live. Settled 18 Aug 2026, and the answer is yes —
     but only because of the two things in the footer below, so neither may be
     quietly dropped:

       - The ENC MARITIME CHART SERVICE (the WMS at egisp.dfo-mpo.gc.ca) is
         published under the OPEN GOVERNMENT LICENCE — CANADA, per its
         geo.ca catalogue record. OGL-Canada permits copying, publishing and
         redistributing in any medium for any lawful purpose, on condition of
         acknowledgement. That is the licence named in the footer.
       - That same record carries the caveat "This service is not for
         navigation", which is why the footer says so in bold.

     DO NOT CONFUSE THIS WITH charts.gc.ca/copyright. That page — "you will
     require a licence to use CHS intellectual property", per-ship counts, no
     further distribution — governs the S-57 ENC and RNC PRODUCTS sold for an
     ECDIS. It is not about the free web service, and reading it as though it
     were is how this question stayed open.

     OGL also forbids implying official status or endorsement, which is the
     other reason the footer names the sources rather than wearing their
     branding. -->

<footer class:indock={inDock}>
  {#if lead}<span class="lead">{@render lead()}</span>{/if}
  CHS charts · CHS tides &amp; streams · DFO fisheries · Open Government Licence — Canada ·
  NOAA charts &amp; tide predictions · bathymetry by
  <a href="https://openwaters.io" rel="noreferrer">Open Waters</a> ·
  <a href="https://openseamap.org" rel="noreferrer">OpenSeaMap</a>
  · Weather data by <a href="https://open-meteo.com/" rel="noreferrer">Open-Meteo</a>
  (<a href="https://creativecommons.org/licenses/by/4.0/" rel="noreferrer">CC BY 4.0</a>),
  adapted by Gennetay Chart, and by <a href="https://www.rainviewer.com/">RainViewer</a>
  · Lightning data: NOAA GOES-19 GLM; adapted by Gennetay Chart
  · <strong>Not for navigation.</strong>
</footer>

<style>
  /* ═══ IT NO LONGER POSITIONS ITSELF, ANYWHERE ═══
   *
   * This was `position: absolute; right: 8px; bottom: 6px` for the desktop
   * case, which is how it came to be drawn on top of the zoom column: two
   * elements anchored independently to the same corner of the same chart.
   *
   * Reserving room for the zoom with a `right: calc(...)` would have fixed the
   * picture and kept the fault — a constant in this file describing another
   * component's rendered width, which is the class of thing the shell exists to
   * end. It is a flex child at both sizes now and the container decides. */
  footer {
    font: var(--fs-micro) / var(--lh-read) var(--sans);
    color: var(--sub);
    /* Legible over an ENC, which is pale sand and pale water rather than a flat
       ground. */
    text-shadow: 0 1px 2px var(--ovl);
    pointer-events: auto;
  }
  footer a {
    color: inherit;
  }
  .lead {
    display: block;
    margin-bottom: 5px;
  }
  footer strong {
    color: var(--am);
    font-weight: inherit;
  }

  /* ═══ IN THE DOCK IT IS A LINE, NOT AN OVERLAY ═══
   *
   * It stops positioning itself entirely. That is the whole point of the dock:
   * the credit used to anchor to `bottom: env(safe-area-inset-bottom)` while
   * the control row anchored to `--gd-controls-bottom` and the sheet stopped at
   * `--gd-footer-h`, three components each holding its own opinion about where
   * the bottom of the screen is. As the dock's last child it simply follows the
   * row above it. */
  footer.indock {
    text-shadow: none;
    /* A legal acknowledgement is a sentence, so keep its reading edge stable. */
    text-align: left;
    font-size: 8px;
    line-height: 1.45;
    padding: 0;
  }

  /* The one place Apple's 44 px is NOT applied.
   *
   * These are 8 px legal text in a two-line credit; at 44 px each they would be
   * the third-largest thing on the screen and would push the chart up. They get
   * a padded hit area instead — about 22 px, comfortably tappable. Stated
   * rather than silently skipped, because "every target is 44" is otherwise a
   * claim this app would be quietly breaking, and `_mobile-proof.mjs` carries
   * the matching exemption by name. */
  @media (pointer: coarse) {
    footer a {
      display: inline-block;
      padding: 6px 3px;
      margin: -6px 0;
    }
  }
</style>
